import React from 'react';
import { ICONS } from '../../constants/imageConstants';
import { cn } from '../../lib/utils';
import { formatDateWithTimezone, formatTimeWithTimezone } from '../../utils/dateUtils';
import { useSelector } from 'react-redux';

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const FormDataCard = React.memo(({ name, location, syncStatus, onView, onEdit, createdAt, coordinates }) => {
  const { isOnline } = useSelector(state => state.ui.networkStatus);
  const isViewDisabled = syncStatus === 'synced' && !isOnline;
  const isPending = syncStatus === 'pending';

  return (
    <div className="rounded-xl border-2 border-gray-200 p-4 overflow-hidden flex flex-col justify-between h-full min-h-[150px] shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 relative">
      <div className="absolute top-0 right-0">
        <span className={`text-[11px] font-bold px-3 py-2 uppercase tracking-wide ${isPending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-700'}`}>
          {isPending ? 'Pending' : 'Synced'}
        </span>
      </div>

      <div className="pr-20 mb-3">
        <h3 className="text-gray-900 text-base font-bold mb-2 line-clamp-2">{name}</h3>
        <div className="flex items-center gap-1.5 mb-2">
          <img src={ICONS.LOCATION_PIN} alt="" className="w-5 h-5" />
          <span className="text-gray-600 text-sm truncate">{location || 'Location not available'}</span>
        </div>
        {createdAt && (
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <CalendarIcon />
              <span>{formatDateWithTimezone(createdAt, coordinates)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ClockIcon />
              <span>{formatTimeWithTimezone(createdAt, coordinates)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto pt-3 border-t border-gray-50 flex gap-2">
        <button
          onClick={onView}
          disabled={isViewDisabled}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs transition-all duration-200',
            'border border-green-600 text-green-700 bg-green-50',
            'active:scale-[0.98] hover:bg-green-100 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <img src={ICONS.VIEW} alt="" className="w-5 h-5" />
          View
        </button>
        {onEdit && (
          <button
            onClick={onEdit}
            disabled={isViewDisabled}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs transition-all duration-200',
              'border border-blue-600 text-blue-700 bg-blue-50',
              'active:scale-[0.98] hover:bg-blue-100 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <EditIcon />
            Edit
          </button>
        )}
      </div>
    </div>
  );
});

FormDataCard.displayName = 'FormDataCard';

export default FormDataCard;